import * as React from 'react';
import { useClickOutside, useToggle } from '@deltasierra/react-hooks';

import './Dropdown.scss';
import classNames from 'classnames';
import { Loading } from '../Generic';

type OptionalShow = {
    show?: boolean;
};

export type CustomDropdownProps = {
    children?: (props: { requestClose: () => void }) => React.ReactNode;
    disabled?: boolean;
    menuLoading?: boolean;
    menuStyle?: React.CSSProperties;
    onOpenChange?: (isOpen: boolean) => void;
    render: (options: { disabled?: boolean; requestToggle: (options?: OptionalShow) => void }) => JSX.Element;
    style?: React.CSSProperties;
    /**
     * By default, the dropdown menu items are aligned along the left side with the parent. This option allows you to
     * change this to be right aligned.
     *
     * @default false
     */
    rightAlign?: boolean;
};

// eslint-disable-next-line react/display-name
export const CustomDropdown: React.FC<CustomDropdownProps> = ({
    children,
    disabled = false,
    menuLoading = false,
    menuStyle,
    onOpenChange,
    render,
    rightAlign = false,
    style,
}) => {
    const [open, dispatch] = useToggle(false);

    const toggle = React.useCallback(
        (options?: OptionalShow) => {
            if (!disabled || open) {
                if (options === undefined || options.show === undefined) {
                    dispatch('toggle');
                } else if (options.show) {
                    dispatch('toggleOn');
                } else {
                    dispatch('toggleOff');
                }
            }
        },
        [dispatch, open, disabled],
    );

    const toggleOff = React.useCallback(() => {
        dispatch('toggleOff');
    }, [dispatch]);

    const onOpenChangeRef = React.useRef(onOpenChange);
    React.useEffect(() => {
        onOpenChangeRef.current = onOpenChange;
    }, [onOpenChange]);

    React.useEffect(() => {
        if (onOpenChangeRef.current) {
            onOpenChangeRef.current(open);
        }
    }, [open]);

    return (
        <div className={classNames('dropdown', { disabled, open })} style={{ width: '100%', ...style }}>
            {render({ disabled, requestToggle: toggle })}
            <DropdownBox rightAlign={rightAlign} show={open} style={menuStyle} onRequestClose={toggleOff}>
                <>
                    {menuLoading && <Loading size="small" />}
                    {!menuLoading && children && children({ requestClose: toggleOff })}
                </>
            </DropdownBox>
        </div>
    );
};

export type DropdownBoxProps = {
    children?: React.ReactNode;
    show: boolean;
    onRequestClose: () => void;
    style?: React.CSSProperties;
    rightAlign?: boolean;
};

const DropdownBox: React.FC<DropdownBoxProps> = props => {
    const [shown, setShown] = React.useState(false);

    React.useEffect(() => {
        setShown(props.show);
    }, [props.show]);

    const ref = useClickOutside<HTMLUListElement>(props.onRequestClose, [props.onRequestClose]);

    return (
        <>
            {shown && (
                <ul
                    className={classNames('dropdown-menu', 'dropdown-menu-box', {
                        'dropdown-menu-right': props.rightAlign ?? false,
                    })}
                    ref={ref}
                    style={props.style}
                >
                    {props.children}
                </ul>
            )}
        </>
    );
};
export type DropdownListItemProps = {
    children?: React.ReactNode;
    className?: string;
    focused?: boolean;
    onClick: () => void;
};

export const DropdownListItem: React.FC<DropdownListItemProps> = ({ focused, ...rest }) => (
    <li {...rest} style={{ cursor: 'pointer', userSelect: 'none' }}>
        <a className={classNames({ 'menu-item-focus': focused })} type="button">
            {rest.children}
        </a>
    </li>
);
